import { Customization, EquipmentConfiguration } from '@/common/types/student';
import { EventBus } from '@/game/GameComponents/PhaserGame/EventBus';
import {
  GameInstance,
  getCharacterInventory,
} from '@/game/GameComponents/PhaserGame/GameInstance';
import { ResponsivenessManager } from '@/game/GameComponents/PhaserGame/Managers/ResponsivenessManager';
import { getSceneManager } from '@/game/GameComponents/PhaserGame/Managers/SceneManager';
import CharacterCreation from '@/game/GameComponents/PhaserGame/Scenes/CharacterCreation/CharacterCreation';
import { SceneLaunchContext } from '@/game/GameComponents/PhaserGame/Types/ManagerTypes/SceneManagerTypes';
import GAME_EVENTS from '@/game/GameComponents/PhaserGame/Utils/gameEventsDef';
import { ItemTabData } from '@/game/GameComponents/PhaserGame/Utils/ItemTabData';
import { ItemScrollGroup } from '@/game/GameComponents/PhaserGame/Utils/PhaserLayout/ItemScrollGroup';
import { getAnchorPlugin } from '@/game/GameComponents/PhaserGame/Utils/PhaserUtils';
import ShopGridItem from '@/game/prefabs/CosmeticShop/ShopGridItem';
import DefaultConfirmButton_m from '@/game/prefabs/DefaultConfirmButton_m';
import DefaultButton from '@/game/prefabs/GeneralUI/Buttons/DefaultButton';
import LevelMap from '@/game/scenes/LevelMap';
import _ from 'lodash';

export class CharacterCreationController {
  constructor(private characterCreation: CharacterCreation) {}

  itemScrollGroup?: ItemScrollGroup;
  itemScrollGroup_m?: ItemScrollGroup;
  nextSceneKey: string = 'LevelMap';
  nextSceneType: typeof Phaser.Scene = LevelMap;
  nextSceneContext: SceneLaunchContext = {
    type: 'gameplay',
    stopActiveScene: true,
    removeSubScenes: true,
  };

  getResponsiveElements(key: keyof CharacterCreation) {
    return ResponsivenessManager.GetAllResponsiveComponentsFromScene(
      this.characterCreation,
      key,
    );
  }

  init(data: any) {
    if (data.nextSceneKey) {
      this.nextSceneKey = data.nextSceneKey;
    }
    if (data.nextSceneType) {
      this.nextSceneType = data.nextSceneType;
    }
    if (data.nextSceneContext) {
      this.nextSceneContext = {
        ...this.nextSceneContext,
        ...data.nextSceneContext,
      };
    }
  }

  async create() {
    // Enable loading panel.
    const { characterCreation } = this;
    this.getResponsiveElements('loadingContainer').forEach(
      (container: Phaser.GameObjects.Container) => container.setVisible(true),
    );

    this.getResponsiveElements('spinnerBlocker').forEach(
      (blocker: Phaser.GameObjects.Image) => blocker.setInteractive(),
    );

    this.getResponsiveElements('submitButton').forEach(
      (button: DefaultButton | DefaultConfirmButton_m) => {
        button.controller.setSubmissionCallback(() => {
          EventBus.emit(GAME_EVENTS.CHARACTER_CREATION_COMPLETED);
          getSceneManager()?.launchScene(
            this.nextSceneKey,
            this.nextSceneType,
            this.nextSceneContext,
          );
        });
      },
    );

    this.setInteractive(false);

    const student = GameInstance.instance.student;

    // Sort by tab order.
    const sortPredicate = (a: ShopGridItem, b: ShopGridItem) => {
      if (a.cosmetic && b.cosmetic) {
        const tabA = ItemTabData.find((d) => d.slot === a.cosmetic?.slot);
        const tabB = ItemTabData.find((d) => d.slot === b.cosmetic?.slot);
        if (!tabA || !tabB) return 0;
        const aIndex = ItemTabData.indexOf(tabA);
        const bIndex = ItemTabData.indexOf(tabB);
        return aIndex < bIndex ? -1 : 1;
      }
      return 0;
    };

    const initialFilter = (c: Customization) =>
      student.characterInventory.unlockedItems.includes(c.id);

    // Desktop scroll group init.
    this.itemScrollGroup = new ItemScrollGroup({
      scene: characterCreation,
      content: characterCreation.content,
      gridBacking: characterCreation.gridBacking,
      scrollBacking: characterCreation.scrollBarBacking,
      scrollHandle: characterCreation.scrollBarHandle,
      showItemPrices: false,
      sortPredicate,
      gridLayoutConfig: {
        scene: characterCreation,
        elements: [],
        columns: 4,
        hPadding: 136,
        vPadding: 136,
        hMargin: 32,
      },
    });

    await this.itemScrollGroup.initialize((item: ShopGridItem) => {
      this.handleItemSelected(item);
      this.updateSelections();
    }, initialFilter);

    this.itemScrollGroup.setItemFilter(initialFilter, () => {
      this.itemScrollGroup?.updateInteractivityWindows();
      this.updateSelections();
      this.getResponsiveElements('loadingContainer').forEach(
        (container: Phaser.GameObjects.Container) =>
          container.setVisible(false),
      );
      this.setInteractive(true);
    });

    // Mobile scroll group init.
    this.itemScrollGroup_m = new ItemScrollGroup({
      scene: characterCreation,
      content: characterCreation.content_m,
      gridBacking: characterCreation.gridBacking_m,
      scrollBacking: characterCreation.scrollBarBacking_m,
      scrollHandle: characterCreation.scrollBarHandle_m,
      showItemPrices: false,
      sortPredicate,
      gridLayoutConfig: {
        scene: characterCreation,
        elements: [],
        columns: 5,
        hPadding: 72,
        vPadding: 72,
        hMargin: 16,
      },
    });

    await this.itemScrollGroup_m.initialize((item: ShopGridItem) => {
      this.handleItemSelected(item);
      this.updateSelections();
    }, initialFilter);

    this.itemScrollGroup_m.setItemFilter(initialFilter, () => {
      this.itemScrollGroup_m?.updateInteractivityWindows();
      this.updateSelections();
      this.getResponsiveElements('loadingContainer').forEach(
        (container: Phaser.GameObjects.Container) =>
          container.setVisible(false),
      );
      this.setInteractive(true);
    });

    // link complexion selections.
    characterCreation.complexionSelection.conjoinWithPicker(
      characterCreation.complexionSelection_m,
    );

    // Initialize random complexion.
    const randomStartingComplexion = _.sample(
      characterCreation.complexionSelection.complexionOptions,
    );
    // We can select it on the desktop picker only because we have
    // conjoined it to the mobile view.
    characterCreation.complexionSelection.handleComplexionSelected(
      randomStartingComplexion,
    );

    this.initializeResponsiveness();
  }

  handleItemSelected(item: ShopGridItem) {
    const equippedItems = getCharacterInventory().equippedItems;

    if (
      Object.keys(equippedItems).find(
        (i) =>
          equippedItems[i as keyof EquipmentConfiguration] ===
          item.cosmetic?.id,
      )
    ) {
      EventBus.emit(GAME_EVENTS.PLAYER_UNEQUIPPED_COSMETIC, item.cosmetic);
    } else {
      EventBus.emit(GAME_EVENTS.PLAYER_EQUIPPED_COSMETIC, item.cosmetic);
    }
  }

  updateSelections() {
    const equippedItems = getCharacterInventory().equippedItems;
    // Set selected item to be highlighted.

    [
      ...(this.itemScrollGroup?.items ?? []),
      ...(this.itemScrollGroup_m?.items ?? []),
    ].forEach((item) => {
      item.setIsSelected(false);
      if (
        Object.values(equippedItems).find(
          (i: number) => i === item.cosmetic?.id,
        ) !== undefined
      ) {
        item.setIsSelected(true);
      }
    });
  }

  setInteractive(isInteractive: boolean) {
    this.getResponsiveElements('submitButton').forEach(
      (button: DefaultButton) =>
        button.controller.makeInteractive(isInteractive),
    );
  }

  initializeResponsiveness() {
    const { characterCreation } = this;

    ResponsivenessManager.InitializeResponsiveBreakpoints({
      scene: characterCreation,
      desktopView: characterCreation.desktopView,
      mobileView: characterCreation.mobileView,
    });

    const anchor = getAnchorPlugin(characterCreation);
    if (anchor) {
      //Desktop responsiveness.
      anchor.add(characterCreation.creationBackground, {
        centerX: '50%',
        centerY: '50%',
      });
      anchor.add(characterCreation.mainContainer, {
        centerX: '50%',
        centerY: '50%',
      });

      // Mobile responsiveness.
      anchor.add(characterCreation.mainContainer_m, {
        centerX: '50%',
        centerY: '50%',
      });
    }
  }
}
